"use client";

import { type RefObject, useEffect, useRef } from "react";

/**
 * Fires `onView` once, the first time the element behind `ref` is at least
 * half on screen. Used for `*_viewed` impression events on cards that sit far
 * below the fold: a mount-time capture counts a card nobody scrolled to as a
 * "view" (jobs-list Pro card: 500 mounts/7d, 0 clicks, 2026-11-03 snapshot),
 * which makes every click-through readout meaningless.
 *
 * Browsers without IntersectionObserver fall back to firing on mount, which
 * is the old behaviour. `active` false (flag off, element not rendered) does
 * nothing.
 */
export function useViewOnce(
	ref: RefObject<Element | null>,
	active: boolean,
	onView: () => void,
): void {
	const firedRef = useRef(false);
	const onViewRef = useRef(onView);
	onViewRef.current = onView;

	useEffect(() => {
		if (!active || firedRef.current) return;
		const fire = () => {
			if (firedRef.current) return;
			firedRef.current = true;
			onViewRef.current();
		};
		const el = ref.current;
		if (!el || typeof IntersectionObserver === "undefined") {
			fire();
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					fire();
					observer.disconnect();
				}
			},
			{ threshold: 0.5 },
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [active, ref]);
}
